import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import { haveColumnsUniqueKeys } from 'components/Table/helpers/haveColumnsUniqueKeys';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import {
  type TableModel,
  type TableOuterStateProps,
  createTableInitialState,
} from 'components/Table/model/TableModel';
import { loadTableSettings } from 'components/Table/model/commands/loadTableSettings';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommandModel } from 'utils/hooks/useCommandModel';
import { useApi } from 'utils/request/useApi';
import { getFilteredColumnMinWidth } from './commands/helpers/getFilteredColumnMinWidth';
import { setTableColumnWidth } from './commands/setTableColumnWidth';
import { setTableRowDetail } from './commands/setTableRowDetail';

type Props<TRow> = TableOuterStateProps<TRow> & {
  onRestoreSettings?: () => void;
  tableCellContext?: Partial<TableCellContext>;
};

export const useTable = <TRow,>(props: Props<TRow>): TableModel => {
  // we want decent generic typing from outside to validate that data match defined columns,
  // but generics inside the component would be insane with little added value.
  const uprops: Props<unknown> = props as Props<unknown>;

  const locale = useLocale();
  const navigate = useNavigate();
  const tableContainerRef = useRef(null);

  const tableCellContext: TableCellContext = useMemo(
    () => ({
      asnFormat: isFeatureFlagEnabled('ENABLE_BGP_ASPLAIN_FORMAT')
        ? 'asplain'
        : 'asdot',
      locale,
      navigate,
      onShowDetailClick: (index: number, expandedColumn?: string) => {
        model.dispatch(setTableRowDetail, { index, expandedColumn });
      },
      ...uprops.tableCellContext,
    }),
    [uprops.tableCellContext, locale],
  );

  const model = useCommandModel(
    () =>
      createTableInitialState(
        props.id,
        uprops.externalSettings,
        uprops.initialSelectedRows,
      ),
    {
      api: useApi(),
      onRestoreSettings: uprops.onRestoreSettings,
    },
    {
      id: uprops.id,
      bulkActions: uprops.bulkActions,
      canRowBeSelected: uprops.canRowBeSelected,
      disableSelectAll: uprops.disableSelectAll,
      externalFilters: uprops.externalFilters,
      onRemoveFilters: uprops.onRemoveFilters,
      columns: uprops.columns,
      data: uprops.data,
      description: uprops.description,
      externalSettings: uprops.externalSettings,
      initialSelectedRows: uprops.initialSelectedRows,
      loadRowIdsForSelectAll: uprops.loadRowIdsForSelectAll,
      loading: uprops.loading,
      menuItems: uprops.menuItems,
      onChangeFilter: uprops.onChangeFilter,
      onChangeIntentFilter: uprops.onChangeIntentFilter,
      onRequestRowRange: uprops.onRequestRowRange,
      onSubmitFilters: uprops.onSubmitFilters,
      reports: uprops.reports,
      rowIdField: uprops.rowIdField,
      selectedFilterPreset: uprops.selectedFilterPreset,
      onSelectFilterPreset: uprops.onSelectFilterPreset,
      showOnlyIntentResultsOnColumns: uprops.showOnlyIntentResultsOnColumns,
      simpleFilterFields: uprops.simpleFilterFields,
      simpleFilterFieldsSubmitted: uprops.simpleFilterFieldsSubmitted,
      sort: uprops.sort,
      onSortChange: uprops.onSortChange,
      rowCount: uprops.rowCount,
      rowCountMore: uprops.rowCountMore,
      tableCellContext,
      title: uprops.title,
      titleEndAdornment: uprops.titleEndAdornment,
      intentFilters: uprops.intentFilters,
      disableRestoreSettings: uprops.disableRestoreSettings,
      disableContextMenu: uprops.disableContextMenu,
      disableStickyColumn: uprops.disableStickyColumn,
      equalWidthColumns: uprops.equalWidthColumns,
      hideAutoSizing: uprops.hideAutoSizing || uprops.equalWidthColumns,
      tableContainerRef,
      snapshotId: uprops.snapshotId ?? null,
      noDataBox: uprops.noDataBox,
    },
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: run only on mount
  useEffect(() => {
    if (model.id !== undefined) {
      model.dispatch(loadTableSettings);
    }
  }, []);

  function stretchSimpleFilteredColumn(columnKey: string) {
    const minWidth = getFilteredColumnMinWidth(
      // Fixed noUncheckedIndexedAccess NIM-19408
      model.simpleFilterFields?.[columnKey]!.filter,
    );
    const columnWidth = model.settings.columnWidth?.[columnKey];

    if (columnWidth && columnWidth < minWidth) {
      model.dispatch(setTableColumnWidth, { columnKey, width: minWidth });
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: handle change of column width only
  useEffect(() => {
    if (
      model.settings.columnWidth !== undefined &&
      model.simpleFilterFields !== undefined
    ) {
      for (const columnKey of Object.keys(model.simpleFilterFields)) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        if (model.simpleFilterFields?.[columnKey]!.value) {
          stretchSimpleFilteredColumn(columnKey);
        }
      }
    }
  }, [model.settings.columnWidth]);

  useEffect(() => {
    if (!haveColumnsUniqueKeys(model.columns)) {
      stumble('Table columns must have unique keys');
    }
  }, [model.columns]);

  return model;
};
